import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { DjayNarrator } from '../../mascot';
import { dataLayer } from '../../data/dataLayer';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
import { soundManager } from '../../utils/soundManager';
import { hapticManager } from '../../utils/hapticManager';
import { ConfettiCelebration } from '../../accounts/components/ConfettiCelebration';
interface TourStep {
  id: string;
  view: string;
  title: string;
  message: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
}
const TOUR_STEPS: TourStep[] = [{
  id: 'tour-home',
  view: 'home',
  title: 'Jouw Dashboard',
  message: 'Hier begin je elke dag! Je ziet hier je dagelijkse doel, je streak en een slimme tip om te oefenen.',
  position: 'right'
}, {
  id: 'tour-modules',
  view: 'modules',
  title: 'Mijn Werelden',
  message: 'Hier vind je alle vakken zoals Rekenen en Taal. Kies een wereld en ontdek nieuwe onderwerpen!',
  position: 'right'
}, {
  id: 'tour-learningLab',
  view: 'learningLab',
  title: 'Het Oefenlab',
  message: 'Wil je gericht trainen voor een toets of extra uitdaging? In het Oefenlab vind je speciale missies.',
  position: 'right'
}, {
  id: 'tour-progress',
  view: 'progress',
  title: 'Jouw Voortgang',
  message: 'Bekijk hier hoe goed je gaat! Je ziet je XP, je groei en de prestaties die je hebt behaald.',
  position: 'right'
}, {
  id: 'tour-collection',
  view: 'collection',
  title: 'Mijn Collectie',
  message: 'Verdien XP om nieuwe looks voor mij te ontgrendelen! Hier kun je me aankleden zoals jij wilt.',
  position: 'right'
}, {
  id: 'tour-settings',
  view: 'settings',
  title: 'Instellingen',
  message: 'Hier kun je geluiden aanpassen, je dagdoel veranderen of deze rondleiding opnieuw bekijken.',
  position: 'right'
}];
interface OnboardingTourProps {
  currentView: string;
  onNavigate: (view: string) => void;
}
export function OnboardingTour({
  currentView,
  onNavigate
}: OnboardingTourProps) {
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [isFinished, setIsFinished] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const {
    shouldAnimate
  } = useMotionBudget();
  // Use a ref for onNavigate so the mount effect doesn't re-run when it changes
  const onNavigateRef = useRef(onNavigate);
  useEffect(() => {
    onNavigateRef.current = onNavigate;
  }, [onNavigate]);
  // 1. Start the tour ONCE on mount if the preference is set.
  const hasCheckedRef = useRef(false);
  useEffect(() => {
    if (hasCheckedRef.current) return;
    hasCheckedRef.current = true;
    const checkTour = async () => {
      try {
        const prefs = await dataLayer.getLearnerPreferences();
        if (prefs.pendingAppTour) {
          setIsActive(true);
          setCurrentStepIndex(0);
          setIsNavigating(true);
          onNavigateRef.current(TOUR_STEPS[0].view);
        }
      } catch {
        /* non-fatal */
      }
    };
    checkTour();
  }, []);
  // 2. Choreograph navigation and spotlight targeting.
  useEffect(() => {
    if (!isActive || isFinished) return;
    const step = TOUR_STEPS[currentStepIndex];
    // If we haven't reached the target view yet, wait.
    if (currentView !== step.view) {
      // Safety: if we aren't navigating but the view is wrong, force it.
      if (!isNavigating) {
        setIsNavigating(true);
        onNavigateRef.current(step.view);
      }
      return;
    }
    // We arrived at the correct view.
    setIsNavigating(false);
    // Wait for the view to render and animations (like AppShell transitions) to settle.
    const timeoutId = setTimeout(() => {
      const el = document.querySelector(`[data-tour="${step.id}"]`);
      if (el) {
        setTargetRect(el.getBoundingClientRect());
      } else {
        setTargetRect(null);
      }
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [isActive, isFinished, currentStepIndex, currentView, isNavigating]);
  // Recompute the target rect on resize.
  useEffect(() => {
    if (!isActive || isFinished || isNavigating) return;
    const handleResize = () => {
      const step = TOUR_STEPS[currentStepIndex];
      const el = document.querySelector(`[data-tour="${step.id}"]`);
      if (el) setTargetRect(el.getBoundingClientRect());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isActive, isFinished, isNavigating, currentStepIndex]);
  const completeTour = async () => {
    try {
      const prefs = await dataLayer.getLearnerPreferences();
      await dataLayer.saveLearnerPreferences({
        ...prefs,
        pendingAppTour: false,
        tourCompleted: true
      });
    } catch {
      /* non-fatal */
    }
    setIsActive(false);
    setIsFinished(false);
    onNavigateRef.current('home');
  };
  const handleNext = () => {
    if (isNavigating) return;
    soundManager.playCorrect();
    hapticManager.hapticMedium();
    if (currentStepIndex < TOUR_STEPS.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);
      setIsNavigating(true);
      setTargetRect(null); // clear old spotlight immediately
      onNavigateRef.current(TOUR_STEPS[nextIndex].view);
    } else {
      soundManager.playLevelUp();
      hapticManager.hapticSuccess();
      setIsFinished(true);
    }
  };
  const handlePrev = () => {
    if (isNavigating) return;
    soundManager.playNavigate();
    hapticManager.hapticLight();
    if (currentStepIndex > 0) {
      const prevIndex = currentStepIndex - 1;
      setCurrentStepIndex(prevIndex);
      setIsNavigating(true);
      setTargetRect(null);
      onNavigateRef.current(TOUR_STEPS[prevIndex].view);
    }
  };
  const handleSkip = async () => {
    soundManager.playNavigate();
    await completeTour();
  };
  if (!isActive) return null;
  const step = TOUR_STEPS[currentStepIndex];
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  // Spotlight cutout geometry (desktop only).
  const spotlightStyle = targetRect ? {
    left: targetRect.left - 8,
    top: targetRect.top - 8,
    width: targetRect.width + 16,
    height: targetRect.height + 16
  } : {
    left: 0,
    top: 0,
    width: 0,
    height: 0
  };
  // Popover position.
  let popoverStyle: React.CSSProperties = {};
  let popoverClass = '';
  if (isMobile) {
    // Mobile: Bottom sheet style, anchored to the bottom of the screen.
    popoverStyle = {
      bottom: 24,
      left: 16,
      right: 16,
      width: 'auto',
      maxWidth: 'none'
    };
    popoverClass = 'items-center text-center';
  } else if (!targetRect) {
    // Desktop fallback: Center.
    popoverStyle = {
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)'
    };
    popoverClass = 'items-center text-center';
  } else {
    // Desktop: Anchor to the spotlight.
    const spaceRight = window.innerWidth - targetRect.right;
    const spaceBottom = window.innerHeight - targetRect.bottom;
    if (step.position === 'right' || spaceRight > 400) {
      popoverStyle = {
        left: targetRect.right + 24,
        top: Math.max(24, targetRect.top - 100)
      };
      popoverClass = 'items-start';
    } else if (step.position === 'bottom' || spaceBottom > 300) {
      popoverStyle = {
        left: Math.max(24, targetRect.left - 150),
        top: targetRect.bottom + 24
      };
      popoverClass = 'items-center text-center';
    } else {
      popoverStyle = {
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)'
      };
      popoverClass = 'items-center text-center';
    }
  }
  return <AnimatePresence data-id="element-2468" data-mp="u22bheueptxp">
      <div className="fixed inset-0 z-[100] pointer-events-auto" data-id="element-2469" data-mp="tyehyfoyp7iz">
        {/* SVG overlay with spotlight cutout (Desktop only) */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" data-id="element-2470" data-mp="kz9hh3tub446">
          <defs data-id="element-2471" data-mp="g17y0hsi52s6">
            <mask id="spotlight-mask" data-id="element-2472" data-mp="g1rhi2xsvz8b">
              <rect width="100%" height="100%" fill="white" data-id="element-2473" data-mp="g2ssy8iqanqk" />
              {targetRect && !isMobile && !isFinished && !isNavigating && <motion.rect initial={false} animate={{
              x: spotlightStyle.left,
              y: spotlightStyle.top,
              width: spotlightStyle.width,
              height: spotlightStyle.height
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30
            }} rx="12" fill="black" data-id="element-2474" data-mp="18pvpww7raqep" />}
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(15, 23, 42, 0.75)" mask={!isMobile ? 'url(#spotlight-mask)' : undefined} className="backdrop-blur-sm" data-id="element-2475" data-mp="1z20j6rsnmcre" />
        </svg>

        {isFinished ? <div className="absolute inset-0 flex items-center justify-center p-4" data-id="element-2476" data-mp="2dyhr191mx997">
            <ConfettiCelebration data-id="element-2477" data-mp="2hxgqwf8iefts" />
            <motion.div initial={shouldAnimate ? {
          opacity: 0,
          scale: 0.9,
          y: 20
        } : false} animate={shouldAnimate ? {
          opacity: 1,
          scale: 1,
          y: 0
        } : false} className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl relative z-10 flex flex-col items-center text-center" data-id="element-2478" data-mp="2hwfgmczbhxd4">
              <div className="w-24 h-24 mb-6" data-id="element-2479" data-mp="oab8an88ec7r">
                <DjayNarrator emotion="celebrating" size="xl" data-id="element-2480" data-mp="ob44iu9kdjbn" />
              </div>
              <h2 className="text-3xl font-black text-slate-900 mb-3" data-id="element-2481" data-mp="t95qpo2adyrc">
                Je bent er klaar voor!
              </h2>
              <p className="text-slate-600 mb-8 text-lg" data-id="element-2482" data-mp="y2qlz8chwemu">
                Heel veel succes en plezier met oefenen. Je kunt deze
                rondleiding altijd nog eens bekijken via Instellingen.
              </p>
              <Button onClick={completeTour} size="lg" className="w-full py-6 text-lg font-bold" data-id="element-2483" data-mp="1c0f8slq4hvuj">
                Start met oefenen!
                <Check className="w-6 h-6 ml-2" data-id="element-2484" data-mp="3drs3khydmncv" />
              </Button>
            </motion.div>
          </div> : <motion.div className={`absolute flex flex-col w-full max-w-[340px] p-4 ${popoverClass}`} style={popoverStyle} initial={shouldAnimate ? {
        opacity: 0,
        y: 20
      } : false} animate={shouldAnimate ? {
        opacity: 1,
        y: 0
      } : false} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 25
      }} data-id="element-2485" data-mp="3iq3buo90wpsz">
            <div className="flex flex-col items-center mb-4" data-id="element-2486" data-mp="3p5ajbes3fxzs">
              <motion.div animate={shouldAnimate ? {
            y: [0, -5, 0]
          } : false} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-2487" data-mp="3k6qv17q12a49">
                <DjayNarrator emotion={isNavigating ? 'thinking' : 'excited'} size="lg" data-id="element-2488" data-mp="vvegth2cdbzf" />
              </motion.div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl border-2 border-orange-100 overflow-hidden w-full" data-id="element-2489" data-mp="lwyoxbvhpyov">
              <div className="p-5" data-id="element-2490" data-mp="qu79fj442eta">
                <div className="flex items-center justify-between mb-2" data-id="element-2491" data-mp="qxcyiycqpgp0">
                  <h3 className="font-black text-lg text-slate-900" data-id="element-2492" data-mp="c5kl4rwiacpx">
                    {step.title}
                  </h3>
                  <span className="text-xs font-bold text-orange-500 bg-orange-100 px-2 py-1 rounded-full" data-id="element-2493" data-mp="gz6xeiyrcel7">
                    {currentStepIndex + 1}/{TOUR_STEPS.length}
                  </span>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed min-h-[40px]" data-id="element-2494" data-mp="3pcw11qd7pgpu">
                  {step.message}
                </p>
              </div>
              <div className="bg-slate-50 p-3 flex items-center justify-between border-t border-slate-100" data-id="element-2495" data-mp="3uanuioti9aw5">
                <button onClick={handleSkip} disabled={isNavigating} className="text-xs font-bold text-slate-400 hover:text-slate-600 px-2 py-2 disabled:opacity-50" data-id="element-2496" data-mp="3aqp5z8fs0o2g">
                  Sla over
                </button>
                <div className="flex gap-2" data-id="element-2497" data-mp="3foifaacdly6y">
                  <Button variant="outline" size="sm" onClick={handlePrev} disabled={currentStepIndex === 0 || isNavigating} className="px-3" data-id="element-2498" data-mp="3tp1hh0u5ua17">
                    <ArrowLeft className="w-4 h-4" data-id="element-2499" data-mp="78ykv7zyy9qb" />
                  </Button>
                  <Button variant="primary" size="sm" onClick={handleNext} disabled={isNavigating} className="px-4 min-w-[100px]" data-id="element-2500" data-mp="2bf1jtoez6ri">
                    {isNavigating ? <Loader2 className="w-4 h-4 animate-spin mx-auto" data-id="element-2501" data-mp="25q8v0ewg8qrc" /> : currentStepIndex === TOUR_STEPS.length - 1 ? 'Klaar' : <>
                        Volgende
                        <ArrowRight className="w-4 h-4 ml-1" data-id="element-2502" data-mp="2kg6nv85bq4mv" />
                      </>}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>}
      </div>
    </AnimatePresence>;
}